import moment from 'moment'
import paths from '../../routes/paths'

const WARNING_EVENT_TYPES = new Set(['low_stock', 'invoice_overdue', 'pos_session_variance'])
const SUCCESS_EVENT_TYPES = new Set(['work_order_completed', 'stock_receipt_posted', 'stock_transfer_posted'])
const SALES_EVENT_TYPES = new Set(['billing_contact_sales', 'landing_demo_request', 'storefront_inquiry'])

function deriveTone(eventType) {
  if (SALES_EVENT_TYPES.has(eventType)) return 'sales'
  if (WARNING_EVENT_TYPES.has(eventType)) return 'warning'
  if (SUCCESS_EVENT_TYPES.has(eventType)) return 'success'
  return 'info'
}

function formatTimeLabel(createdAt) {
  if (!createdAt) return ''
  return moment(createdAt).fromNow()
}

/** Normalizes one inbox notification row from the API to the UI shape. */
export function responseToItem(row) {
  return {
    id: row.id,
    title: row.title ?? '',
    message: row.body ?? '',
    eventType: row.event_type ?? '',
    source: row.source ?? 'event',
    unread: row.read_at === null || row.read_at === undefined,
    readAt: row.read_at ?? null,
    createdAt: row.created_at ?? '',
    timeLabel: formatTimeLabel(row.created_at),
    tone: deriveTone(row.event_type ?? ''),
    dataType: row.data?.type ?? '',
    dataId: row.data?.id ?? '',
  }
}

/** Maps notification data.type + data.id to an SPA path. Keep in sync with buildNavPath in public/firebase-messaging-sw.js */
export function getNavPath(dataType, dataId, eventType = '', { isSuperAdmin = false } = {}) {
  const type = String(dataType || '').toLowerCase()
  const event = String(eventType || '').toLowerCase()
  const companyId = String(dataId || '')

  if (isSuperAdmin && (event === 'billing_contact_sales' || event === 'landing_demo_request' || type === 'demo_request' || type === 'sales_request')) {
    if (type === 'demo_request' || type === 'sales_request' || event === 'landing_demo_request') {
      return companyId ? paths.admin.salesLeads.detail(companyId) : paths.admin.salesLeads.list
    }
    return companyId ? paths.admin.companyHealth(companyId) : paths.admin.salesLeads.list
  }

  if (isSuperAdmin && type === 'company' && companyId) {
    return paths.admin.companyHealth(companyId)
  }

  if (!companyId) {
    return paths.notifications
  }

  switch (type) {
    case 'invoice':
      return paths.invoices.detail(companyId)
    case 'work_order':
      return paths.workOrders.edit(companyId)
    case 'item':
      return paths.products.edit(companyId)
    case 'stock_receipt':
      return paths.stockReceipts.detail(companyId)
    case 'transfer':
      return paths.stockTransfers.detail(companyId)
    case 'purchase_order':
      return paths.purchaseOrders.detail(companyId)
    case 'vendor_bill':
      return paths.vendorBills.detail(companyId)
    case 'estimate':
      return paths.estimates.edit(companyId)
    case 'payment':
      return paths.payments.edit(companyId)
    case 'pos_session':
      return paths.pos
    case 'storefront_inquiry':
      return paths.storefrontInquiries.detail(companyId)
    case 'storefront_review':
      return paths.settings.productReviews
    default:
      return paths.notifications
  }
}

export function getNotificationPresentation(item = {}) {
  const eventType = String(item.eventType || '').toLowerCase()
  const dataType = String(item.dataType || '').toLowerCase()
  const tone = deriveTone(eventType)

  if (eventType === 'landing_demo_request' || eventType === 'billing_contact_sales' || dataType === 'demo_request' || dataType === 'sales_request') {
    return {
      tone,
      actionLabel: 'View request',
      actionLabelKey: 'notifications_action_view_request',
    }
  }

  if (dataType === 'company') {
    return {
      tone,
      actionLabel: 'View company',
      actionLabelKey: 'notifications_action_view_company',
    }
  }

  if (dataType && item.dataId) {
    return {
      tone,
      actionLabel: 'Open',
      actionLabelKey: 'notifications_action_open',
    }
  }

  return { tone, actionLabel: null, actionLabelKey: null }
}
